import React, { useState } from "react";
import {
  ShieldAlert, AlertTriangle, AlertOctagon, CheckCircle2, Clock, Info, Gavel, Layers, FileText,
  Volume2, VolumeX, Search, ChevronLeft, ChevronRight, Loader2, Ban,
} from "lucide-react";
import { useI18n } from "../lib/i18n";

// ---------- Severity / Status badges (icon + label + text, never color-only) ----------
const SEV = {
  Critical: { icon: ShieldAlert, cls: "bg-red-50 text-red-700 border-red-200", label: "CRITICAL" },
  High: { icon: AlertOctagon, cls: "bg-orange-50 text-orange-700 border-orange-200", label: "HIGH" },
  Medium: { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "MEDIUM" },
  Low: { icon: Info, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "LOW" },
  Info: { icon: Info, cls: "bg-slate-50 text-slate-600 border-slate-200", label: "INFO" },
};
export function SeverityBadge({ level, testid }) {
  const s = SEV[level] || SEV.Info;
  const Icon = s.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
}

const STATUS_CLS = {
  "COMPLIANT": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Passed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Approved": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "COMPLETED": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Closed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Active": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Production": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "NON-COMPLIANT": "bg-red-50 text-red-700 border-red-200",
  "Failed": "bg-red-50 text-red-700 border-red-200",
  "Rejected": "bg-red-50 text-red-700 border-red-200",
  "BLOCKED": "bg-red-50 text-red-700 border-red-200",
  "Blocked": "bg-red-50 text-red-700 border-red-200",
  "Findings Open": "bg-orange-50 text-orange-700 border-orange-200",
  "Open": "bg-orange-50 text-orange-700 border-orange-200",
  "Pending": "bg-amber-50 text-amber-700 border-amber-200",
  "PENDING APPROVAL": "bg-amber-50 text-amber-700 border-amber-200",
  "PARTIAL": "bg-amber-50 text-amber-700 border-amber-200",
  "IN PROGRESS": "bg-blue-50 text-blue-700 border-blue-200",
  "In Review": "bg-blue-50 text-blue-700 border-blue-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
};
export function StatusBadge({ status, testid }) {
  const cls = STATUS_CLS[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

const REG_STATUS = {
  LAW: { icon: Gavel, cls: "bg-purple-50 text-purple-700 border-purple-200" },
  MANDATORY: { icon: AlertOctagon, cls: "bg-red-50 text-red-700 border-red-200" },
  "NATIONAL FRAMEWORK": { icon: Layers, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  FRAMEWORK: { icon: Layers, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  GUIDELINE: { icon: FileText, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  VOLUNTARY: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  DRAFT: { icon: Clock, cls: "bg-slate-100 text-slate-600 border-slate-300" },
  CONSULTATION: { icon: Clock, cls: "bg-slate-100 text-slate-600 border-slate-300" },
};
export function RegStatusBadge({ status, testid }) {
  const s = REG_STATUS[status] || REG_STATUS.GUIDELINE;
  const Icon = s.icon;
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${s.cls}`}><Icon className="h-3.5 w-3.5" /> {status}</span>;
}

// ---------- Stat card ----------
export function StatCard({ label, value, icon: Icon, accent, onClick, testid, sub }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick}
      className={`group text-left rounded-2xl border border-slate-200 bg-white p-5 card-shadow transition-all ${onClick ? "hover:card-shadow-lg hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold font-head text-slate-900 truncate">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        {Icon && <div className={`rounded-xl p-2.5 ${accent || "bg-[var(--qatar-subtle)] text-[var(--qatar)]"}`}><Icon className="h-5 w-5" /></div>}
      </div>
    </button>
  );
}

// ---------- Page header + read aloud ----------
export function PageHeader({ title, subtitle, children, summary }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between animate-fade-up">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-head tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">
        <ReadAloud text={summary || `${title}. ${subtitle || ""}`} />
        {children}
      </div>
    </div>
  );
}

export function ReadAloud({ text, testid = "read-aloud-global-button" }) {
  const { lang, t } = useI18n();
  const [speaking, setSpeaking] = useState(false);
  const speak = () => {
    if (!window.speechSynthesis) return;
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "ar" ? "ar-SA" : "en-US";
    u.onend = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };
  return (
    <button data-testid={testid} onClick={speak} title={t("read_aloud")}
      className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${speaking ? "border-[var(--qatar-border)] bg-[var(--qatar-subtle)] text-[var(--qatar)]" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
      {speaking ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      <span className="hidden sm:inline">🔊</span>
    </button>
  );
}

// ---------- Data table with search/pagination ----------
export function DataTable({ columns, rows, onRowClick, testid, pageSize = 20, searchable = true }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const filtered = q
    ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()))
    : rows;
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const view = filtered.slice(page * pageSize, page * pageSize + pageSize);
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white card-shadow overflow-hidden">
      {searchable && (
        <div className="border-b border-slate-100 p-3">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input data-testid="table-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
              placeholder={t("search")} className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 ps-9 pe-3 text-sm outline-none focus:border-[var(--qatar)] focus:ring-2 focus:ring-[var(--qatar-border)]" />
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60 text-start">
              {columns.map((c) => <th key={c.key} className="px-4 py-3 text-start text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={r.id || i} data-testid={`row-${r.id || i}`} onClick={() => onRowClick && onRowClick(r)}
                className={`border-b border-slate-50 ${onRowClick ? "cursor-pointer hover:bg-[var(--qatar-subtle)]/40" : ""}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.render ? c.render(r) : r[c.key]}</td>)}
              </tr>
            ))}
            {view.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400">No records</td></tr>}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
          <span>{filtered.length} records · page {page + 1}/{pages}</span>
          <div className="flex gap-1">
            <button data-testid="page-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-200 p-1.5 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4 flip-x" /></button>
            <button data-testid="page-next" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-200 p-1.5 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4 flip-x" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Loading() {
  return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>;
}

export function SandboxTag({ text = "SIMULATED RESULT – SANDBOX MODE" }) {
  return <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800"><Ban className="h-3.5 w-3.5" /> {text}</span>;
}

export function Card({ title, children, className = "", right }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 md:p-6 card-shadow ${className}`}>
      {(title || right) && <div className="mb-4 flex items-center justify-between"><h3 className="text-base md:text-lg font-semibold font-head text-slate-900">{title}</h3>{right}</div>}
      {children}
    </div>
  );
}

export const CHART_COLORS = ["#8A1538", "#D4AF37", "#2563EB", "#059669", "#EA580C", "#7C3AED", "#0891B2", "#DC2626"];
